<template>
  <div class="hasDropzone" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
    <div v-if="uploadedFiles.length > 0" class="file-preview-container">
      <div v-for="(file, index) in uploadedFiles" :key="index" class="file-preview">
        <div v-if="file.isImage">
          <img :src="file.preview" alt="Image preview" class="file-thumb" />
        </div>
        <div v-else class="file-info">
          <div class="file-placeholder">
            <small>{{ file.extension }}</small>
            <br />
            {{ file.name }}
          </div>
        </div>
        <button class="remove-btn" @click="removeFile(index)">
          <span class="sr-only">{{ $t("sr.close") }}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="#FFF" viewBox="0 0 256 256"><path d="M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z"></path></svg>
        </button>
      </div>
    </div>

    <s-form class="mt-n4 mb-n3" @submit="submitForm">
      <s-form-input
        v-model="message"
        id="message"
        name="message"
        :label="inputPlaceholder"
        label-sr-only
        :placeholder="inputPlaceholder"
        :class="{ 'dropzone-active': isDragOver }"
      >
        <template #input-group-append>
          <b-input-group-append>
            <b-button type="submit" variant="warning">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" class="bi bi-send-fill" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471z"/>
              </svg>
              <span :class="conversationSid ? 'sr-only' : 'ml-2'">{{ conversationSid ? $t("sr.send-message") : $t("sr.send-first-message") }}</span>
            </b-button>
            <b-button v-if="conversationSid" type="button" variant="outline-primary" @click="triggerFileUpload">
              <b-icon icon="paperclip" aria-hidden="true"></b-icon>
              <span class="attach-file-label sr-only">{{ $t("sr.upload-files") }}</span>
            </b-button>
          </b-input-group-append>
        </template>
      </s-form-input>
      <input ref="fileInput" type="file" style="display: none" multiple @change="handleFileUpload" />

      <div v-if="isDragOver" class="dropzone">
        {{ $t("label.drop-files") }}
      </div>
    </s-form>
    <!-- Limite fixée à 20 Mo pour couvrir la plupart des photos prises par des smartphones -->
    <small class="text-muted text-center d-block">{{ $t("notice.max-file-size") }}</small>
  </div>
</template>

<script>
import SForm from "@/components/form/s-form";
import SFormInput from "@/components/form/s-form-input";
import TwilioService from "@/services/twilio";
import NotificationService from "@/services/notification";
import { DANGEROUS_MIME_TYPES } from "@/consts/mime-types";

export default {
  data() {
    return {
      message: "",
      isDragOver: false,
      uploadedFiles: []
    };
  },
  components: {
    SForm,
    SFormInput
  },
  computed: {
    inputPlaceholder() {
      return this.$t("placeholder.new-message", {
        user: this.otherParticipantName
      });
    }
  },
  props: {
    conversationSid: {
      type: String
    },
    conversationId: {
      type: String
    },
    otherParticipantName: {
      type: String,
      required: true
    },
    ratingRequestSentAt: {
      type: String
    }
  },
  methods: {
    triggerFileUpload() {
      this.$refs.fileInput.click();
    },

    handleFileUpload(event) {
      const files = event.target.files;
      if (files.length > 0) {
        let totalBusted = false;
        let totalSize = this.uploadedFiles.reduce((acc, file) => acc + file.file.size, 0);

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const reader = new FileReader();
          const fileExtension = file.name.split(".").pop();
          const isImage = file.type.startsWith("image/");

          if (DANGEROUS_MIME_TYPES.includes(file.type)) {
            NotificationService.showError(this.$t("notification.conversation-no-dangerous-files"));
            continue;
          }

          totalSize += file.size;
          // Max total size is 20MB
          if (totalSize > 20 * 1024 * 1024) {
            totalSize -= file.size;
            totalBusted = true;
            continue;
          }

          if (isImage) {
            reader.onload = (e) => {
              this.uploadedFiles.push({
                file: file,
                name: file.name,
                extension: fileExtension,
                preview: e.target.result,
                isImage: true
              });
            };
            reader.readAsDataURL(file);
          } else {
            this.uploadedFiles.push({
              file: file,
              name: file.name,
              extension: fileExtension,
              isImage: false
            });
          }
        }

        if (totalBusted) {
          NotificationService.showError(this.$t("notification.conversation-file-size-limit"));
        }
      }
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    onDrop(event) {
      event.stopPropagation();
      const files = event.dataTransfer.files;
      if (files.length > 0) {
        this.isDragOver = false;
        this.handleFileUpload({ target: { files } });
      }
    },

    onDragOver(event) {
      event.stopPropagation();
      this.isDragOver = true;
    },

    onDragLeave(event) {
      event.stopPropagation();
      this.isDragOver = false;
    },

    async submitForm() {
      if (this.message !== "" || this.uploadedFiles.length > 0) {
        const mediaFiles = await Promise.all(
          this.uploadedFiles.map(async (uploadedFile) => {
            const fileBlob = await uploadedFile.file.arrayBuffer();
            return {
              contentType: uploadedFile.file.type,
              filename: uploadedFile.file.name,
              media: new Blob([fileBlob], { type: uploadedFile.file.type })
            };
          })
        );

        if (this.conversationSid) {
          await TwilioService.addMessageToConversation({
            sid: this.conversationSid,
            conversationId: this.conversationId,
            body: this.message,
            medias: mediaFiles,
            ratingRequestSentAt: this.ratingRequestSentAt
          });
        } else {
          this.$emit("sendMessage", this.message);
        }

        this.message = "";
        this.uploadedFiles = [];
      }
    }
  }
};
</script>

<style scoped lang="scss">
.hasDropzone {
  position: relative;
}
.dropzone {
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  color: rgb(0, 0, 0);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 18px;
  z-index: 10;
  border: 2px dashed rgba(0, 0, 0, 0.5);
}

.file-preview-container {
  display: flex;
  flex-wrap: wrap;
  padding-bottom: 15px;
}

.file-preview {
  position: relative;
  margin-right: 10px;
  cursor: default;

  &:hover {
    opacity: 1;
  }
}

.file-thumb:hover {
  transform: scale(1);
}

.file-thumb,
.file-info {
  width: 100px;
  height: 100px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.file-info {
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0;
  text-align: center;
  font-size: 12px;
  padding: 10px;
}

.remove-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  background-color: $red-darker;
  color: white;
  border: none;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: $red;
  }

  & > svg {
    width: 14px; 
    height: 14px;
  }
}

.attach-file-label {
  @include media-breakpoint-up(sm) {
    position: static;
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
    margin-left: 4px;
  }
}
</style>